"use client";

import { useState, useRef, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

const socialScript = `I pay $17.99/month for @StreamFlixPremium and they're STILL running ads during my shows.

This is the streaming double-dip and it ends now.

I'm switching to @ClearStream until they stop billing me twice.

#PaidMeansPaid 🚫💳`;

const videoScript = `[HOOK - 0-3s]
"You're paying $18/month for streaming... and still seeing ads?"

[PROBLEM - 3-15s]
"Here's what's happening. Streaming platforms found a loophole. They charge you a subscription fee — that's payment #1 — then they ALSO sell your attention to advertisers — that's payment #2. You're being billed twice."

[SOLUTION - 15-25s]
"The PaidMeansPaid movement is simple: if a brand runs ads on a tier you PAY for, you immediately switch to their direct competitor and you tell them why, publicly."

[CTA - 25-30s]
"Download the free toolkit at PaidMeansPaid.com. Join 214,000 people who are done being double-billed. Link in bio."

[TEXT OVERLAY] #PaidMeansPaid`;

export default function ToolkitSection() {
  const [copied, setCopied] = useState<"social" | "video" | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [stampVisible, setStampVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".toolkit-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.remove("opacity-0-init");
                card.classList.add("animate-fade-up");
              }, i * 180);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCopy = async (type: "social" | "video") => {
    const text = type === "social" ? socialScript : videoScript;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2500);
    } catch {

      // fallback
    }};

  const handleDownload = () => {
    setStampVisible(true);
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 3000);
  };

  return (
    <section
      id="toolkit"
      ref={sectionRef}
      className="section-ink py-24 md:py-32 px-6 md:px-12 relative overflow-hidden"
      aria-label="Action toolkit">
      
      {/* Background texture */}
      <div className="noise-overlay absolute inset-0 z-0 pointer-events-none opacity-50" />

      <div className="relative z-10 max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-xl">
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF2222] block mb-4">
              Action Center
            </span>
            <h2 className="font-display text-5xl md:text-6xl font-black text-surface leading-[0.95] tracking-tightest">
              The Protest<br />
              <span className="italic font-normal text-[#FF2222]">Toolkit.</span>
            </h2>
          </div>
          <p className="text-base text-white/50 max-w-sm font-light leading-relaxed">
            Copy-paste ready scripts, downloadable graphics, and video templates. Everything you need to apply pressure.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Card 1: Social Media Scripts */}
          <div className="toolkit-card opacity-0-init lg:col-span-1 bg-white/5 border border-white/10 rounded-4xl p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#FF2222]/20 flex items-center justify-center">
                <Icon name="ChatBubbleOvalLeftEllipsisIcon" size={18} variant="outline" className="text-[#FF2222]" />
              </div>
              <div>
                <h3 className="font-semibold text-surface text-sm">Social Media Scripts</h3>
                <span className="text-xs text-white/40">Twitter / X · Threads · Instagram</span>
              </div>
            </div>

            <pre className="script-block flex-1 mb-4 text-xs overflow-auto">{socialScript}</pre>

            <button
              onClick={() => handleCopy("social")}
              className={`flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-sm font-semibold transition-all duration-300 ${
              copied === "social" ? "bg-green-500 text-white" : "bg-[#FF2222] text-white hover:bg-[#CC0000]"}`
              }
              aria-label="Copy social media script to clipboard">
              
              {copied === "social" ?
              <>
                  <Icon name="CheckIcon" size={16} variant="outline" className="animate-check-pop" />
                  Copied! Now go post it.
                </> :

              <>
                  <Icon name="ClipboardDocumentIcon" size={16} variant="outline" />
                  Copy to Clipboard
                </>
              }
            </button>
          </div>

          {/* Card 2: Red Stamp Graphic */}
          <div className="toolkit-card opacity-0-init lg:col-span-1 bg-white/5 border border-white/10 rounded-4xl p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#FF2222]/20 flex items-center justify-center">
                <Icon name="PhotoIcon" size={18} variant="outline" className="text-[#FF2222]" />
              </div>
              <div>
                <h3 className="font-semibold text-surface text-sm">The Red Stamp Graphic</h3>
                <span className="text-xs text-white/40">PNG overlay for screenshots</span>
              </div>
            </div>

            {/* Preview */}
            <div className="relative flex-1 rounded-2xl overflow-hidden bg-white/10 mb-4 min-h-[180px] flex items-center justify-center">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_163ae73a8-1771563627039.png"
                alt="Streaming service interface showing unwanted advertisements on a paid subscription tier"
                className="w-full h-full object-cover opacity-40"
                fill />
              
              {stampVisible &&
              <div className="absolute inset-0 flex items-center justify-center animate-stamp">
                  <div className="stamp-text text-4xl md:text-5xl">
                    DOUBLE DIPPING
                  </div>
                </div>
              }
              {!stampVisible &&
              <div className="absolute inset-0 flex items-center justify-center">
                  <div className="stamp-text text-3xl opacity-60 rotate-[-12deg]">
                    PAID MEANS PAID
                  </div>
                </div>
              }
            </div>

            <button
              onClick={handleDownload}
              className={`flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-sm font-semibold transition-all duration-300 ${
              downloaded ?
              "bg-green-500 text-white" : "bg-[#FF2222] text-white hover:bg-[#CC0000]"}`
              }
              aria-label="Download red stamp graphic overlay">
              
              {downloaded ?
              <>
                  <Icon name="CheckIcon" size={16} variant="outline" />
                  Download Started!
                </> :

              <>
                  <Icon name="ArrowDownTrayIcon" size={16} variant="outline" />
                  Download Image
                </>
              }
            </button>
          </div>

          {/* Card 3: Video Scripts */}
          <div className="toolkit-card opacity-0-init lg:col-span-1 bg-white/5 border border-white/10 rounded-4xl p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-[#FF2222]/20 flex items-center justify-center">
                <Icon name="VideoCameraIcon" size={18} variant="outline" className="text-[#FF2222]" />
              </div>
              <div>
                <h3 className="font-semibold text-surface text-sm">Video Scripts</h3>
                <span className="text-xs text-white/40">TikTok · Reels · YouTube Shorts</span>
              </div>
            </div>

            <pre className="script-block flex-1 mb-4 text-xs overflow-auto max-h-[220px]">{videoScript}</pre>

            <button
              onClick={() => handleCopy("video")}
              className={`flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-sm font-semibold transition-all duration-300 ${
              copied === "video" ? "bg-green-500 text-white" : "bg-white/10 text-surface hover:bg-white/20 border border-white/20"}`
              }
              aria-label="Copy video script to clipboard">
              
              {copied === "video" ?
              <>
                  <Icon name="CheckIcon" size={16} variant="outline" className="animate-check-pop" />
                  Script Copied!
                </> :

              <>
                  <Icon name="ClipboardDocumentIcon" size={16} variant="outline" />
                  Copy Script
                </>
              }
            </button>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-xs text-white/30 mt-8 font-light">
          All assets are free to use. No attribution required. Spread them everywhere.
        </p>
      </div>
    </section>);

}